import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Form } from '@epages/react-components'
import type { Map as ImmutableMap } from 'immutable'
import { fromJS } from 'immutable'
import { kebabCase } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import cc from 'classcat'

import { GTM_BODY_SNIPPET_RE, GTM_HEAD_SNIPPET_RE } from '../../utils/googleTagManager'
import { getDefaultValue } from './FooterSettings'
import { previewFavicon, previewUserSettings, setFavicon, setUserSettings } from '../../store/actions'
import { useTranslate } from '../../utils/translate'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import CookieSettings from './CookieSettings'
import FaviconSettings from './FaviconSettings'
import GoogleTagManagerSettings from './GoogleTagManagerSettings'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'

// Beware: Key is also used as translation key and content access key
const tabs = {
  cookieNotice: CookieSettings,
  googleTagManager: GoogleTagManagerSettings,
  faviconSettings: FaviconSettings,
}

function looksLikeGTMHeadHtml(str: string) {
  return GTM_HEAD_SNIPPET_RE.test(str)
}

function looksLikeGTMBodyHtml(str: string) {
  return GTM_BODY_SNIPPET_RE.test(str)
}

interface StorefrontSettingsProps {
  readonly active: string | boolean
  readonly onToggle: (options?: { submit?: boolean }) => void
}

export default function StorefrontSettings({ active, onToggle }: StorefrontSettingsProps) {
  const dispatch = useDispatch()
  // Omit Google Analytics setting, which is set via MBO
  const userSettings = useSelector<State, any>((state) =>
    state.getIn(['shop', 'userSettings']).delete('googleAnalytics'),
  )
  const faviconSettings = useSelector<State, any>((state) =>
    fromJS({
      faviconUrl: state.getIn(['shop', 'faviconUrl']) || '',
    }),
  )
  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))

  const t = useTranslate('interface')
  const [activeTab, setActiveTab] = useState<string>('cookieNotice')
  const initialFaviconSettings = useRef(faviconSettings)
  const initialUserSettings = useRef(userSettings)

  // Resets any unsaved local changes (changes that are not affecting data that is also used in other bottom
  // bar setting layers). Shared bottom bar settings changes are reset in the parent `BottomBar` component.
  const resetUnsavedLocalChanges = useCallback(() => {
    dispatch(previewFavicon(initialFaviconSettings.current.toJS()))
  }, [dispatch])

  useEffect(() => {
    // Change active tab when a deeplink is set
    setActiveTab(typeof active === 'string' ? active : 'cookieNotice')

    return () => {
      resetUnsavedLocalChanges()
    }
  }, [active, resetUnsavedLocalChanges])

  const onSubmit = async (data: ImmutableMap<string, any>) => {
    const updatedUserSettings = data.delete('faviconSettings')
    await dispatch(setUserSettings(updatedUserSettings.toJS()))

    const updatedFaviconSettings = data.get('faviconSettings')
    await dispatch(setFavicon(updatedFaviconSettings.toJS()))

    initialUserSettings.current = updatedUserSettings
    initialFaviconSettings.current = updatedFaviconSettings

    // send storefront settings to MBO
    window.parent?.postMessage(
      {
        type: 'STOREFRONT_SETTINGS',
        payload: {
          faviconUrl: data.getIn(['faviconSettings', 'faviconUrl']),
        },
      },
      mboBaseUrl,
    )

    onToggle({ submit: true })
  }

  const onChange = (data: ImmutableMap<string, any>) => {
    requestAnimationFrame(() => {
      if (activeTab !== 'faviconSettings') {
        dispatch(previewUserSettings(data.delete('faviconSettings')))
      }

      if (activeTab === 'faviconSettings') {
        dispatch(previewFavicon(data.get('faviconSettings').toJS()))
      }
    })

    // Make sure not to return any data in order to prevent 'smart' data handling
    // https://github.com/ePages-de/react-components/blob/v0.4.3/src/form/Form.jsx#L142-L143
    return null
  }

  const tScoped = (subKey: string, ...args: any[]) => t('components.bottomBar.storefrontSettings.' + subKey, ...args)

  return (
    <>
      <button
        type="button"
        className={cc(['ep-bottom-bar-button', 'ep-bottom-bar-button-shop-settings', { checked: active }])}
        title={tScoped('title')}
        onClick={() => onToggle()}
      ></button>
      <SettingsLayer
        className={cc(['ep-settingslayer-shop-settings', `${kebabCase(activeTab)}-active`])}
        onEscapeKeyDown={() => onToggle()}
        open={!!active}
        side="top"
        align="center"
      >
        {({ renderLayout }) => (
          <Form
            name="userSettings"
            value={userSettings.set('faviconSettings', faviconSettings)}
            prepare={(data) =>
              data.mergeDeep(
                ...Object.entries(tabs).map(([key, tab]) =>
                  getDefaultValue(
                    {
                      userSettings,
                      shopLocale: '',
                      footerPages: fromJS([]),
                      shopResellerName: '',
                      taxCountry: '',
                      t: tScoped,
                    },
                    [key, tab],
                  ),
                ),
                data,
              )
            }
            onChange={onChange}
            validate={(values) => {
              const errors: any = {}

              const headHtml = values.getIn(['googleTagManager', 'headHtml'], '')
              if (headHtml.trim() && !looksLikeGTMHeadHtml(headHtml)) {
                errors.googleTagManager = errors.googleTagManager || {}
                errors.googleTagManager.headHtml = tScoped(
                  'googleTagManager.headInputField.validationMessages.invalidGoogleTagManagerSnippet',
                )
              }

              const bodyHtml = values.getIn(['googleTagManager', 'bodyHtml'], '')
              if (bodyHtml.trim() && !looksLikeGTMBodyHtml(bodyHtml)) {
                errors.googleTagManager = errors.googleTagManager || {}
                errors.googleTagManager.bodyHtml = tScoped(
                  'googleTagManager.bodyInputField.validationMessages.invalidGoogleTagManagerSnippet',
                )
              }

              return Object.keys(errors).length > 0 ? fromJS(errors) : undefined
            }}
            onSubmit={onSubmit}
          >
            {renderLayout({
              title: tScoped(`${activeTab}.title`),
              tabs: Object.keys(tabs).map((tab) => ({
                name: tab,
                title: tScoped(`${tab}.title`),
                active: activeTab === tab,
                onClick: () => setActiveTab(tab),
              })),
              body: React.createElement(tabs[activeTab]),
              footer: (
                <ButtonRow>
                  <Button
                    type="button"
                    variant="cancel"
                    onClick={() => {
                      resetUnsavedLocalChanges()
                      onToggle()
                    }}
                  >
                    {tScoped('cancelButton.label')}
                  </Button>
                  <Button type="submit">{tScoped('applyButton.label')}</Button>
                </ButtonRow>
              ),
            })}
          </Form>
        )}
      </SettingsLayer>
    </>
  )
}
